import React, {useState, useRef, useEffect} from 'react'
import Text from '../../../components/text/Text'
import {IPromotionItem, createPromotionItem, SlotType} from '../../../apis/promotion/item'
import img_lot_main from '../../../assets/images/img_lot_main.jpg'
import WizImage from '../../../components/image/WizImage'
import {useImage} from '../../../utils/image'
import {postUploadImage} from '../../../apis/image/postUploadImage'
import {message} from 'antd'

// Slot별 권장 사이즈 정의
const SLOT_SIZE_CONFIG: Record<string, {pc?: {width: number; height: number}; mo?: {width: number; height: number}}> = {
    'leaderboard-banner': {
        pc: {width: 1554, height: 600},
        mo: {width: 960, height: 300},
    },
    'tradermap-card': {
        pc: {width: 1800, height: 450},
        mo: {width: 990, height: 300},
    },
    'landing-event': {
        pc: {width: 2560, height: 400},
        mo: {width: 640, height: 200},
    },
    'popup-event': {
        pc: {width: 800, height: 700},
        mo: {width: 800, height: 700},
    },
    'announce-event': {
        pc: {width: 960, height: 450},
        mo: {width: 960, height: 450},
    },
    'bottom-sheet': {
        pc: {width: 1350, height: 810},
        mo: {width: 1350, height: 810},
    },
    'account-banner': {
        pc: {width: 960, height: 240},
        mo: {width: 960, height: 240},
    },
    'leaderboard-header': {
        pc: {width: 960, height: 240},
        mo: {width: 960, height: 240},
    },
}

interface IProps {
    items: IPromotionItem[]
    onEdit: (item: IPromotionItem) => void
    promotionId: string
    onRefresh: () => void
}

export function PromotionSlotList({items, onEdit, promotionId, onRefresh}: IProps) {
    const {renderImage} = useImage()
    const [isDragging, setIsDragging] = useState(false)
    const [uploading, setUploading] = useState(false)
    const dragCounter = useRef(0)
    const [imageSizes, setImageSizes] = useState<Record<number, {width: number; height: number}>>({})
    const loadingRef = useRef<Set<number>>(new Set())

    // 권장 width 가져오기
    const getExpectedWidth = (slot: string, device: string): number | undefined => {
        const config = SLOT_SIZE_CONFIG[slot]
        return config?.[device as 'pc' | 'mo']?.width
    }

    // 이미지 실제 사이즈 로드 (권장 width로 리사이즈된 이미지 기준)
    useEffect(() => {
        items.forEach(item => {
            // 이미 로드했거나 로드 중인 경우 스킵
            if (!item.image_source || imageSizes[item.id] || loadingRef.current.has(item.id)) {
                return
            }

            const expectedWidth = getExpectedWidth(item.slot, item.device)
            if (!expectedWidth) return

            loadingRef.current.add(item.id)
            const img = new Image()
            img.onload = () => {
                setImageSizes(prev => ({
                    ...prev,
                    [item.id]: {width: img.naturalWidth, height: img.naturalHeight},
                }))
                loadingRef.current.delete(item.id)
            }
            img.onerror = () => {
                loadingRef.current.delete(item.id)
            }
            // URL에 ?width= 파라미터 추가
            const separator = item.image_source.includes('?') ? '&' : '?'
            img.src = `${item.image_source}${separator}width=${expectedWidth}`
        })
    }, [items, imageSizes])

    // 사이즈 비율 일치 여부 확인 (권장 width로 리사이즈된 이미지의 height가 맞는지)
    const checkSizeMatch = (
        item: IPromotionItem,
    ): {match: boolean; expected?: {width: number; height: number}; actual?: {width: number; height: number}} => {
        const config = SLOT_SIZE_CONFIG[item.slot]
        const deviceConfig = config?.[item.device as 'pc' | 'mo']
        const actualSize = imageSizes[item.id]

        if (!deviceConfig || !actualSize) {
            return {match: true}
        }

        // 권장 width로 리사이즈된 이미지의 height가 권장 height와 일치하는지 확인
        const match = actualSize.height === deviceConfig.height

        return {match, expected: deviceConfig, actual: actualSize}
    }

    // Helper: Map Filename to Slot & Device
    // Format: "[SlotValue]_[Device].png"
    // e.g. "leaderboardTop_PC.png" -> slot: "leaderboard-banner", device: "pc"
    const getIdFromFilename = (filename: string) => {
        const nameWithoutExt = filename.substring(0, filename.lastIndexOf('.'))
        const parts = nameWithoutExt.split('_')

        if (parts.length < 2) return null

        const rawSlotValue = parts[0] // e.g., leaderboardTop
        const rawDevice = parts[1].toLowerCase() // e.g., pc, mobileWeb, app

        let slotId = ''
        // Map UI Value/Filename Prefix -> Backend ID
        // Based on user mapping
        const slotMap: Record<string, string> = {
            leaderboardTop: 'leaderboard-banner',
            traderMapTop: 'tradermap-card',
            landingBanner: 'landing-event',
            landingPopup: 'popup-event',
            announceEvent: 'announce-event',
            bottomSheet: 'bottom-sheet',
            accountBanner: 'account-banner',
            leaderboardHeader: 'leaderboard-header',
            postAnnouncement: 'post-announcement',
        }

        // Find matching key case-insensitively or exact match
        const matchedKey = Object.keys(slotMap).find(k => k.toLowerCase() === rawSlotValue.toLowerCase())
        if (matchedKey) {
            slotId = slotMap[matchedKey]
        } else {
            return null // Unknown slot type
        }

        // Map Device
        let device = 'pc'
        if (rawDevice.includes('app') || rawDevice.includes('mobile') || rawDevice === 'mo') {
            device = 'mo'
        }

        return {slot: slotId, device}
    }

    const handleDragEnter = (e: React.DragEvent) => {
        e.preventDefault()
        e.stopPropagation()
        console.log('Drag Enter')
        dragCounter.current += 1
        if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
            setIsDragging(true)
        }
    }

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault()
        e.stopPropagation()
        console.log('Drag Leave')
        dragCounter.current -= 1
        if (dragCounter.current === 0) {
            setIsDragging(false)
        }
    }

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault()
        e.stopPropagation()
        console.log('Drag Over')
        if (!uploading) {
            e.dataTransfer.dropEffect = 'copy'
            if (!isDragging) setIsDragging(true) // Ensure visible if missed enter
        }
    }

    const handleDrop = async (e: React.DragEvent) => {
        e.preventDefault()
        e.stopPropagation()
        console.log('Dropped file', e.dataTransfer.files)

        // Reset drag state
        setIsDragging(false)
        dragCounter.current = 0

        if (!promotionId) {
            message.error('Promotion ID is missing.')
            console.error('Promotion ID is missing')
            return
        }

        const files = Array.from(e.dataTransfer.files)
        if (files.length === 0) return

        setUploading(true)
        let successCount = 0

        try {
            for (const file of files) {
                console.log('Processing file:', file.name)
                // 1. Parse Filename
                const info = getIdFromFilename(file.name)
                if (!info) {
                    message.warning(`Skipped invalid file format: ${file.name}`)
                    console.warn('Invalid format for:', file.name)
                    continue
                }

                // 2. Upload Image
                // FIXED: Do not ask for resize/fit here. Get raw URL.
                // renderImage will add optimization params on display.
                const uploadRes = await postUploadImage(file, false, false)

                if (uploadRes?.image_url) {
                    // 3. Create Slot
                    const payload = {
                        promotion_set_id: Number(promotionId),
                        slot: info.slot as SlotType,
                        device: info.device,
                        image_source: uploadRes.image_url,
                        background: '#ffffff', // Default white bg
                        data: {
                            url: '', // Add default URL if needed
                        },
                    }

                    console.log('Creating item with payload:', payload)
                    const res = await createPromotionItem(promotionId, payload)

                    if (res?.data?.success || res?.data?.id) {
                        successCount++
                        console.log('Success creating item')
                    } else {
                        message.error(`Failed to create slot for ${file.name}`)
                        console.error('Failed create res:', res)
                    }
                } else {
                    message.error(`Failed to upload image for ${file.name}`)
                    console.error('Failed upload res:', uploadRes)
                }
            }

            if (successCount > 0) {
                message.success(`Successfully added ${successCount} slots!`)
                onRefresh()
            }
        } catch (error) {
            console.error(error)
            message.error('An error occurred during upload.')
        } finally {
            setUploading(false)
        }
    }

    // Grid configuration
    const getSlotLabel = (slot: string) => {
        const map: Record<string, string> = {
            'leaderboard-banner': 'Leaderboard Top',
            'tradermap-card': 'Trader Map Top',
            'landing-event': 'Landing Banner',
            'popup-event': 'Landing Popup',
            'announce-event': 'Announce Event',
            'bottom-sheet': 'BottomSheet',
            'account-banner': 'Account Banner',
            'leaderboard-header': 'Leaderboard Header',
            postAnnouncement: 'post-announcement',
        }
        return map[slot] || slot
    }

    const getGridClass = (slot: string, device: string) => {
        const key = slot.toLowerCase()
        if (key.includes('leaderboard-banner') || key.includes('leaderboardtop')) return 'col-span-12 row-span-3'
        if (key.includes('leaderboard-header')) return 'col-span-12 row-span-2'
        if (key.includes('tradermap')) return 'col-span-12 md:col-span-8 row-span-2'
        if (key.includes('popup')) return 'col-span-6 md:col-span-4 row-span-4'
        if (key.includes('bottom-sheet')) return 'col-span-6 md:col-span-4 row-span-4'
        if (key.includes('landing')) return 'col-span-6 md:col-span-6 row-span-2'
        if (key.includes('announce')) return 'col-span-6 md:col-span-4 row-span-2'
        if (key.includes('account')) return 'col-span-6 md:col-span-4 row-span-1'
        return 'col-span-6 md:col-span-4 row-span-2'
    }

    const renderSlotPreview = (item: IPromotionItem, extraClass: string) => {
        const isLeaderboard = item.slot.toLowerCase().includes('leaderboard')
        const isPopup = item.slot.toLowerCase().includes('popup')
        const isBottomSheet = item.slot.toLowerCase().includes('bottom-sheet')

        return (
            <div
                key={item.id}
                className={`group relative cursor-pointer hover:shadow-xl transition-all ${extraClass}`}
                onClick={() => onEdit(item)}>
                {/* 1. Leaderboard Style Preview */}
                {isLeaderboard && (
                    <div
                        className="w-full h-auto relative overflow-hidden rounded-[5px] flex items-center justify-center"
                        style={{backgroundColor: item.background || '#000'}}>
                        {item.image_source ? (
                            <WizImage
                                src={renderImage(item.image_source)}
                                className="w-full h-auto object-cover"
                                containerClassName="w-full"
                                alt={item.slot}
                            />
                        ) : (
                            <Text className="text-white opacity-20 text-h1 font-bold">LEADERBOARD</Text>
                        )}
                    </div>
                )}

                {/* 2. Popup Style Preview */}
                {isPopup && (
                    <div className="w-full h-full relative bg-gray-800/50 backdrop-blur-sm rounded-[10px] flex items-center justify-center p-4 border border-white/10">
                        <div className="relative shadow-2xl rounded-[10px] overflow-hidden max-h-full max-w-full">
                            {item.image_source ? (
                                <WizImage
                                    src={renderImage(item.image_source)}
                                    className="w-full h-full object-contain"
                                    alt={item.slot}
                                />
                            ) : (
                                <div className="bg-white w-[200px] h-[300px] flex items-center justify-center">
                                    <Text className="text-gray-300 font-bold">POPUP</Text>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* 3. Bottom Sheet Style Preview */}
                {isBottomSheet && (
                    <div
                        className="w-full h-[30vw] md:h-[50vh] relative overflow-hidden rounded-[10px] flex flex-col justify-end"
                        style={{backgroundColor: '#000'}}>
                        <div className="absolute inset-0 opacity-50">
                            <WizImage src={img_lot_main} className="w-full h-auto object-cover grayscale" />
                        </div>
                        <div className="relative z-10 w-full pt-4 rounded-t-xl overflow-hidden ">
                            {item.image_source ? (
                                <WizImage
                                    src={renderImage(item.image_source)}
                                    className="w-full h-auto object-cover rounded-tl-[15px] rounded-tr-[15px]"
                                    alt={item.slot}
                                />
                            ) : (
                                <div className="h-[200px] flex items-center justify-center text-gray-400">
                                    <Text>BOTTOM SHEET</Text>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* 4. Default/Other Style */}
                {!isLeaderboard && !isPopup && !isBottomSheet && (
                    <div className="w-full h-auto relative rounded-[10px] overflow-hidden bg-gray-100 border border-line02">
                        {item.image_source ? (
                            <WizImage
                                src={renderImage(item.image_source)}
                                className="w-full h-auto object-cover"
                                alt={item.slot}
                            />
                        ) : (
                            <div className="flex items-center justify-center h-full">
                                <Text className="text-gray-300 font-bold uppercase">{item.slot.split('-')[0]}</Text>
                            </div>
                        )}
                    </div>
                )}

                {/* Common Overlays (Badge + Edit Hover) */}
                <div className="absolute top-2 left-2 z-20">
                    <span className="bg-black/70 backdrop-blur-md text-white text-[10px] font-bold px-2 py-1 rounded shadow-sm border border-white/10">
                        {getSlotLabel(item.slot)} {item.device ? `(${item.device?.toUpperCase()})` : ''}
                    </span>
                </div>

                {/* Size Overlay */}
                {(() => {
                    const sizeCheck = checkSizeMatch(item)
                    const actualSize = imageSizes[item.id]
                    if (!actualSize) return null
                    return (
                        <div className="absolute top-2 right-2 z-20">
                            <span
                                className={`backdrop-blur-md text-[10px] font-bold px-2 py-1 rounded shadow-sm border ${
                                    sizeCheck.match
                                        ? 'bg-black/70 text-white border-white/10'
                                        : 'bg-red-500/90 text-white border-red-300'
                                }`}>
                                {actualSize.width}x{actualSize.height}
                                {!sizeCheck.match && sizeCheck.expected && (
                                    <Text className="ml-1 text-[10px] text-white inline">
                                        (Guide: {sizeCheck.expected.width}x{sizeCheck.expected.height})
                                    </Text>
                                )}
                            </span>
                        </div>
                    )
                })()}

                <div className="absolute inset-0 bg-primary/20 opacity-0 group-hover:opacity-100 transition-opacity z-30 flex items-center justify-center border-2 border-primary rounded-[10px] pointer-events-none">
                    <span className="bg-white text-primary font-bold px-4 py-1.5 rounded-full shadow-lg transform scale-90 group-hover:scale-100 transition-transform">
                        Edit Slot
                    </span>
                </div>
            </div>
        )
    }

    return (
        <div
            className={`relative min-h-[300px] transition-colors rounded-[10px] ${
                isDragging ? 'bg-primary/5 ring-2 ring-primary ring-inset' : ''
            }`}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}>
            {/* Drag Overlay - FIXED: Now handles events directly to prevent bubbling issues */}
            {(isDragging || uploading) && (
                <div
                    className="absolute inset-0 z-50 rounded-[10px] flex items-center justify-center backdrop-blur-sm"
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}>
                    <div className="text-center p-8 bg-white/90 rounded-xl shadow-xl pointer-events-none">
                        {uploading ? (
                            <>
                                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto mb-4" />
                                <Text className="text-h3 font-bold text-primary">Uploading & Creating Slots...</Text>
                                <Text className="text-font02 mt-2">Please wait...</Text>
                            </>
                        ) : (
                            <>
                                <Text className="text-h2 font-bold text-primary mb-2">Drop Files Here</Text>
                                <Text className="text-font02">
                                    Auto-create slots from filenames
                                    <br />
                                    (e.g., leaderboardTop_PC.png)
                                </Text>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Empty State */}
            {(!items || items.length === 0) && (
                <div className="p-10 bg-white border-2 border-dashed border-line02 rounded-[5px] text-center hover:border-primary/50 transition-colors pointer-events-none">
                    <Text className="text-body2 text-font03">No Slots found. Add one above or Drop files here.</Text>
                </div>
            )}

            {/* Grid Content */}
            <div
                className={`columns-2 md:columns-1 gap-4 pointer-events-none justify-between ${
                    !items || items.length === 0 ? 'hidden' : ''
                }`}>
                {items?.map(item => {
                    const gridClass = getGridClass(item.slot, item.device)
                    // Allow pointer events on items so we can edit
                    return renderSlotPreview(
                        item,
                        `${gridClass} pointer-events-auto break-inside-avoid mb-4 ${
                            isDragging ? 'opacity-50 scale-95' : ''
                        }`,
                    )
                })}
            </div>
        </div>
    )
}
